'use client'

import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'

// What the lean bar means (2026-10-24): a small ⓘ that opens a short note on
// click or tap (hover alone doesn't exist on phones), closed by a second
// click, Escape or a click elsewhere. It explains the label it sits next to,
// with this story's own numbers (Sam, 2026-10-24: the general rules were
// overkill, and the note should read in the bar's context), then where the
// ratings come from. `label` is { key, text } from selectionCoverage; null on
// topic pages, which show no label.
function explain(label, pct, total, subject = 'this story') {
  const n = total.toLocaleString()
  const split = `${pct.left}% left, ${pct.center}% center, ${pct.right}% right`
  if (!label)
    return `These ${n} articles split ${split}, by each outlet's lean.`
  // No label before the sentence (it sits right beside the ⓘ), and the
  // numbers follow the order the sentence names the sides in.
  const l = `${pct.left}%`
  const r = `${pct.right}%`
  switch (label.key) {
    case 'balanced':
      return `The left and the right covered ${subject} about equally: ${l} and ${r} of the articles.`
    case 'leans_left':
      return `The left covered ${subject} more than the right: ${l} of the articles vs. ${r}.`
    case 'leans_right':
      return `The right covered ${subject} more than the left: ${r} of the articles vs. ${l}.`
    case 'left':
      return `The left covered ${subject} far more than the right: ${l} of the articles vs. ${r}.`
    case 'right':
      return `The right covered ${subject} far more than the left: ${r} of the articles vs. ${l}.`
    case 'center':
      return `Most of the articles came from center outlets (${pct.center}%).`
    default:
      return `${label.text}: these ${n} articles split ${split}.`
  }
}

export default function LeanBarInfo({
  total,
  pct,
  label = null,
  // The topic's name on a topic page ("The right covered Ilhan Omar…").
  subject = 'this story',
}) {
  const [open, setOpen] = useState(false)
  const box = useRef(null)

  useEffect(() => {
    if (!open) return
    const away = (e) => {
      if (box.current && !box.current.contains(e.target)) setOpen(false)
    }
    const esc = (e) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('pointerdown', away)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('pointerdown', away)
      document.removeEventListener('keydown', esc)
    }
  }, [open])

  return (
    <span ref={box} className="relative inline-flex align-middle">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label="What this bar shows"
        className="ml-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full border border-muted-foreground/50 font-sans text-[10px] font-semibold leading-none text-muted-foreground transition-colors hover:border-foreground hover:text-foreground"
      >
        i
      </button>
      {open && (
        <span
          role="dialog"
          aria-label="What this bar shows"
          className="absolute left-0 top-full z-30 mt-2 w-64 rounded-sm border border-border bg-card p-3 text-left font-sans text-xs leading-relaxed text-foreground shadow-md"
        >
          <span className="block">{explain(label, pct, total, subject)}</span>
          <span className="mt-2 block text-muted-foreground">
            Each outlet&apos;s lean comes from AllSides, Ad Fontes Media and
            Media Bias/Fact Check.
          </span>
          <Link
            href="/how-it-works#sources"
            className="mt-2 inline-block text-muted-foreground underline decoration-foreground/40 underline-offset-2 hover:text-foreground"
          >
            How it works &rarr;
          </Link>
        </span>
      )}
    </span>
  )
}
